import { RegionShared } from "@1games-ru/region-shared";
import { InventoryOwnerType } from "@1games-ru/region-shared/dist/enums/inventory/inventory";
import { useEffect, useState } from "react";
import rpc from "@1games-ru/rage-rpc";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { IS_DEV } from "@/domains/main/constants/other";
import { reset, setSafe } from "@/domains/inventory/slice";
import { playerInfoSelector } from "@/domains/player/slice";
import useGetEnvironment from "@/domains/inventory/hooks/useGetEnvironment";
import useGetInventory from "@/domains/inventory/hooks/useGetInventory";

export default function useShowInventory() {
  const [isShowInventory, setIsShowInventory] = useState(false);
  const { getInventory } = useGetInventory();
  const { getEnvironment } = useGetEnvironment();
  const dispatch = useAppDispatch();
  const playerInfo = useAppSelector(playerInfoSelector);

  useEffect(() => {
    rpc.register(
      RegionShared.Cef.Events.Inventory.ShowInventory,
      async (data: typeof RegionShared.Cef.Requests.ShowInventoryRequest) => {
        dispatch(reset());
        if (!playerInfo) return;
        const { inventoryOwnerId, inventoryOwnerType } = data;

        const promises = [];

        promises.push(
          getInventory({
            inventoryOwnerId,
            inventoryOwnerType,
          }),
        );

        if (inventoryOwnerType !== InventoryOwnerType.Character) {
          promises.push(
            getInventory({
              inventoryOwnerId: String(playerInfo.character.id),
              inventoryOwnerType: InventoryOwnerType.Character,
            }),
          );
        }

        await Promise.all(promises);

        setIsShowInventory(true);
        return true;
      },
    );

    return () => {
      rpc.unregister(RegionShared.Cef.Events.Inventory.ShowInventory);
    };
  }, [playerInfo]);

  useEffect(() => {
    rpc.register(RegionShared.Cef.Events.Inventory.HideInventory, () => {
      dispatch(reset());

      setIsShowInventory(false);
    });
    return () => {
      rpc.unregister(RegionShared.Cef.Events.Inventory.HideInventory);
    };
  }, []);

  useEffect(() => {
    if (!playerInfo) return;

    rpc.register(
      RegionShared.Cef.Events.Inventory.InventorySafeOpen,
      async (data: typeof RegionShared.Cef.Requests.InventorySafeOpenRequest) => {
        dispatch(reset());
        const { items } = data;

        dispatch(setSafe(data));

        await Promise.all([
          getInventory({
            inventoryOwnerId: String(playerInfo.character.id),
            inventoryOwnerType: InventoryOwnerType.Character,
          }),
          getInventory({
            inventoryOwnerId: String(items[0].id),
            inventoryOwnerType: InventoryOwnerType.EstateSafe,
          }),
        ]);

        setIsShowInventory(true);
      },
    );
    return () => {
      rpc.unregister(RegionShared.Cef.Events.Inventory.InventorySafeOpen);
    };
  }, [playerInfo]);

  useEffect(() => {
    rpc.register(RegionShared.Cef.Events.Inventory.InventorySafeClose, async () => {
      dispatch(reset());

      setIsShowInventory(false);
    });

    return () => {
      rpc.unregister(RegionShared.Cef.Events.Inventory.InventorySafeClose);
    };
  }, []);

  useEffect(() => {
    rpc.register(RegionShared.Cef.Events.Observer.NotifyInventoryUpdateEnvironment, async () => {
      getEnvironment();
    });

    return () => {
      rpc.unregister(RegionShared.Cef.Events.Observer.NotifyInventoryUpdateEnvironment);
    };
  }, []);

  return {
    isShowInventory: !!(isShowInventory || IS_DEV),
  };
}
